import React from "react";
import { FormControl, FormLabel, TextField } from "@mui/material";
import {
  Controller,
  FieldError,
  FieldErrorsImpl,
  Merge,
  useFormContext,
} from "react-hook-form";

interface InputFieldRegProps {
  fieldName: string;
  label?: string;
  fullWidth?: boolean;
  control: any;
  error?: boolean;
  helperText?:
  | string
  | null
  | undefined
  | FieldError
  | Merge<FieldError, FieldErrorsImpl<any>>;
  defaultValue?: string | null;
  onBlur?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onChange?: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  other?: any;
  multiline?: boolean | undefined;
  isDisable?: boolean;
  isTextGrey?: boolean;
  style?: object;
  labelColor?: string;
  required?: boolean;
  yupSchema?: any;
  inputLabelProps?: any;
}

const InputFieldReg: React.FC<InputFieldRegProps> = ({
  fieldName,
  control,
  label,
  error,
  helperText,
  fullWidth,
  defaultValue,
  onBlur,
  onChange,
  other,
  multiline,
  isDisable = false,
  isTextGrey = true,
  style = {},
  required = false,
  labelColor,
  yupSchema,
  inputLabelProps,
}) => {
  const formContext = useFormContext();
  const { getFieldState } = formContext || {};

  // compute required flag:
  // priority: explicit required prop -> yupSchema (if provided) -> fallback to getFieldState (best-effort)
  const computeIsRequired = React.useCallback(() => {
    if (required) return true;

    try {
      if (yupSchema) {
        // prefer using describe() when available
        if (typeof yupSchema.describe === "function") {
          const desc = yupSchema.describe();
          const f = desc?.fields?.[fieldName];
          if (f?.tests) {
            return f.tests.some((t: any) => t?.name === "required");
          }
        }
        const fieldSchema =
          yupSchema.fields?.[fieldName] ?? yupSchema?.[fieldName];
        if (fieldSchema) {
          const tests = fieldSchema.tests ?? fieldSchema._tests ?? [];
          if (Array.isArray(tests)) {
            return tests.some(
              (t: any) => t?.name === "required" || t?.TEST === "required"
            );
          }
        }
      }
    } catch {
      // ignore
    }

    try {
      const fieldState = getFieldState ? getFieldState(fieldName) : null;
      return !!fieldState?.invalid;
    } catch {
      return false;
    }
  }, [required, yupSchema, fieldName, getFieldState]);

  const [isRequiredState, setIsRequiredState] = React.useState<boolean>(() =>
    computeIsRequired()
  );
  React.useEffect(() => {
    setIsRequiredState(computeIsRequired());
  }, [computeIsRequired]);

  const commonTextFieldStyles = {
    "& .MuiOutlinedInput-notchedOutline": {
      borderWidth: error ? "0px" : "2px",
      borderColor: error ? "transparent" : "primary.main",
      borderRadius: "8px",
      color: "secondary.main",
      overflow: "hidden",
    },
    height: multiline ? "100%" : 38,
    backgroundColor: "rgba(63, 141, 124, 0.00)",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: error ? "transparent" : "primary.main",
    },
    "& .MuiInputLabel-shrink": {
      transform: "translate(14px, -6px) scale(0.85)",
    },
    ...style,
  };

  const getLabelWithAsterisk = () => {
    if (!label) return "";
    return isRequiredState ? `${label} *` : label;
  };

  return (
    <FormControl sx={{ width: "100%" }}>
      {labelColor && (
        <FormLabel
          sx={{ mt: 1, color: labelColor ? labelColor : "secondary.main" }}
          htmlFor={`${label}`}
        >
          {getLabelWithAsterisk()}
        </FormLabel>
      )}
      <Controller
        name={fieldName}
        control={control}
        defaultValue={defaultValue || ""}
        render={({ field }) => (
          <TextField
            {...field}
            multiline={multiline}
            size="small"
            variant={isTextGrey ? "outlined" : "standard"}
            label={labelColor ? "" : getLabelWithAsterisk()}
            fullWidth={fullWidth || true}
            error={error}
            helperText={helperText || ""}
            onBlur={(
              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            ) => {
              field.onBlur();
              onBlur && onBlur(event);
            }}
            onChange={(
              event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
            ) => {
              const value = event.target.value;
              field.onChange(value);
              onChange && onChange(event);
            }}
            disabled={isDisable}
            InputLabelProps={{
              sx: {
                color: "secondary.main",
                background: "none",
                padding: "0 5px",
              },
              ...inputLabelProps,
            }}
            {...other}
            InputProps={{
              sx: commonTextFieldStyles,
              ...other?.InputProps,
            }}
          />
        )}
      />
    </FormControl>
  );
};

export default InputFieldReg;
